/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */

import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { GlobalStyle } from 'styles/global-styles';
import { NotFoundPage } from './components/AtomComponents/NotFoundPage/Loadable';
import jwtDecode from 'jwt-decode';
import { useDispatch, useSelector } from 'react-redux';
import { useAuthPageSlice } from './pages/Auth/slice';
import { RootState } from 'types';
import { JwtPayload } from './pages/Auth/slice/types';
import { Navbar } from './components/Navbar/Loadable';
import { Inbox } from './pages/Inbox/Loadable';
import { Auth } from './pages/Auth/Loadable';
import { OAuthForm } from 'app/components/AppStoreComponent/Integrations/CustomOAuth/OAuthForm/Loadable';
import { ResetPassword } from './components/ResetPassword';
import { UpdatePassword } from './components/UpdatePassword';
import { AcceptInvite } from './components/AcceptInvite';
import { FullPageIframe } from 'app/components/AppStoreComponent/Integrations/CustomOAuth/FullPageIframe/Loadable';
import { AutoLogin } from 'app/components/AppStoreComponent/Integrations/CustomOAuth/AutoLogin/Loadable';
import { NewNavbar } from './components/NewNavbar/Loadable';
import { BillingBanner } from './components/BillingBanner';
import useMediaQuery from 'utils/hooks/useMediaQuery';
import { Register } from './pages/Register/Loadable';
import { ToastContainer } from 'react-toastify';
import { Settings } from './pages/Settings/Loadable';
import { Users } from './pages/Users/Loadable';
import { AppStore } from './pages/AppStore/Loadable';
import { Templates } from './pages/Templates/Loadable';
import { LambdaStudio } from './pages/LambdaStudio/Loadable';
import {
  createClientObject,
  inboxPageActions,
  useInboxPageSlice,
} from './pages/Inbox/slice';
import { errorModal } from 'utils/showSuccessErrorModal';
import { Message, Client, Template } from './pages/Inbox/slice/types';
import { logPageView } from 'utils/googleAnalytics';
import { useSocket } from 'utils/context/SocketContext';
import { useMobileNavigation } from 'utils/context/NavigateContext';
import { getDomainConfig } from 'utils/whiteLabel';
import {
  getActiveToken,
  getTabToken,
  setIdentityToken,
  setTabToken,
  clearAllTokens,
} from 'utils/tabAuth';
import {
  embedBootStatus,
  embedReloadTarget,
  isEmbedPath,
  EmbedBootStatus,
} from 'utils/embedBoot';
import api from 'app/api';
import { BulkImportTemplate } from './components/AppStoreComponent/BulkImportTemplate/Loadable';
import { ChooseBusiness } from './components/ChooseBusiness/Loadable';
import { Loader } from './components/AtomComponents/Loader';
import { useTabBusiness } from 'utils/hooks/useTabBusiness';
import { Chatbot } from './pages/Chatbot/Loadable';
import { MetaAI } from './pages/MetaAI/Loadable';
import { useCallsSlice } from './pages/Calls/slice';
import { CallStatusType, ReceivedCallType } from './pages/Calls/slice/types';
import { CallsComponent } from './components/CallsComponent/Loadable';
import { Calls } from './pages/Calls/Loadable';
import { TokenExpirationPopup } from './components/AtomComponents/TokenExpirationPopup';
import { ReferralComponent } from './components/ReferralComponent/Loadable';
import { Admin } from './pages/Admin/Loadable';
import { Docs } from './components/DocsComponent/Loadable';
import { PartnerDashboard } from './components/PartnerDashboard/Loadable';
import { CurlDevPanel } from './components/CurlDevPanel/Loadable';
import { TestWidgetHost } from './components/TestWidgetHost';
import { TestWidgetLauncher } from './components/TestWidgetHost/TestWidgetLauncher';
import { useIOSViewportFix } from 'utils/hooks/useIOSViewportFix';
import { useWindowEvent } from 'utils/hooks/useWindowEvent';
import { useTranslation } from 'react-i18next';

export function App() {
  const { t } = useTranslation();
  const location = useLocation();
  const config = getDomainConfig();

  // Docs page - minimal render, no hooks, no navbar
  if (location.pathname.startsWith('/docs')) {
    return (
      <>
        <Helmet
          titleTemplate={config.appName}
          defaultTitle={t('Documentation')}
        >
          <meta name='description' content={t('Documentation')} />
        </Helmet>
        <Routes>
          <Route path='/docs/*' element={<Docs />} />
        </Routes>
        <CurlDevPanel />
        <GlobalStyle />
      </>
    );
  }

  if (isEmbedPath(location.pathname)) {
    return <EmbedAppContent />;
  }

  // Partner dashboard - separate shell, no inbox/socket/navbar
  if (config.isPartnerDashboard) {
    return <PartnerAppContent />;
  }

  // Main app - full functionality
  return <MainAppContent />;
}

function PartnerAppContent() {
  const { t } = useTranslation();
  const state = useSelector((state: RootState) => state?.authPage?.authData);
  const location = useLocation();
  const config = getDomainConfig();

  React.useEffect(() => {
    logPageView(location.pathname, document.title);
  }, [location]);

  useIOSViewportFix();
  useTokenVerification();

  const isLoggedIn = state && state.isLoggedIn;

  const helmet = (
    <Helmet
      titleTemplate={config.appName}
      defaultTitle={t('Partner Dashboard')}
    >
      <meta name='description' content={t('Partner Dashboard')} />
    </Helmet>
  );

  if (!isLoggedIn) {
    return (
      <>
        {helmet}
        <Navbar />
        <Routes>
          <Route path='/' element={<Auth />} />
          <Route path='/reset-password' element={<ResetPassword />} />
          <Route path='/reset-password/:token' element={<UpdatePassword />} />
          <Route path='/accept-invite/:token' element={<AcceptInvite />} />
          <Route path='*' element={<Navigate to='/' />} />
        </Routes>
        <CurlDevPanel />
        <GlobalStyle />
        <ToastContainer />
      </>
    );
  }

  return (
    <>
      {helmet}
      <main className='overflow-auto h-[var(--app-height,100vh)]'>
        <PartnerDashboard />
      </main>
      <CurlDevPanel />
      <GlobalStyle />
      <ToastContainer />
    </>
  );
}

function MainAppContent() {
  const { t } = useTranslation();
  const state = useSelector((state: RootState) => state?.authPage?.authData);
  const { showSection } = useMobileNavigation();
  const isMobile = useMediaQuery('(max-width: 980px)');
  const location = useLocation();
  const config = getDomainConfig();

  React.useEffect(() => {
    logPageView(location.pathname, document.title);
  }, [location]);

  useIOSViewportFix();
  useDisablePinchZoom();
  useTokenVerification();
  useSocketMessages();
  const tabBusiness = useTabBusiness(Boolean(state?.isLoggedIn));

  const isLoggedIn = state && state.isLoggedIn;
  const showNewNavbar =
    isLoggedIn &&
    (!isMobile || (showSection !== 1 && showSection !== 2)) &&
    (!isMobile || !config?.isThreeDotNavbarForMobile);

  const helmet = (
    <Helmet
      titleTemplate={getDomainConfig().appName}
      defaultTitle={t('Complete WhatsApp CRM')}
    >
      <meta name='description' content='A Complete WhatsApp CRM application' />
    </Helmet>
  );

  if (!isLoggedIn) {
    return (
      <>
        {helmet}
        <Navbar />
        <AppRoutes isLoggedIn={false} authData={state} />
        <CurlDevPanel />
        <GlobalStyle />
        <ToastContainer />
      </>
    );
  }

  if (tabBusiness.status === 'booting') {
    return (
      <>
        {helmet}
        <div className='flex h-[var(--app-height,100vh)] items-center justify-center bg-[var(--heltar-bg-surface)]'>
          <Loader />
        </div>
        <GlobalStyle />
      </>
    );
  }

  return (
    <>
      {helmet}
      <div className='flex flex-col h-[var(--app-height,100vh)] overflow-hidden'>
        <BillingBanner />
        <div
          className={`flex ${isMobile ? 'flex-col' : 'flex-row'} flex-1 overflow-hidden [contain:layout]`}
        >
          {showNewNavbar && <NewNavbar />}
          <main className='flex-1 overflow-hidden relative z-0'>
            <AppRoutes isLoggedIn={true} authData={state} />
          </main>
        </div>
      </div>
      <CallsComponent />
      <TokenExpirationPopup />
      <CurlDevPanel />
      {/* Keeps the in-dashboard test widget mounted on every page + across
          reloads while the admin has it toggled on (Settings → Web Chat Widget). */}
      <TestWidgetHost />
      <TestWidgetLauncher />
      <GlobalStyle />
      <ToastContainer />
    </>
  );
}

const AppRoutes = ({
  isLoggedIn,
  authData,
}: {
  isLoggedIn?: boolean;
  authData?: JwtPayload;
}) => {
  if (isLoggedIn) {
    return (
      <Routes>
        <Route path='/' element={<Navigate to='/inbox' />} />
        <Route path='/inbox' element={<Inbox />} />
        <Route path='/calls' element={<Calls />} />
        <Route path='/settings/*' element={<Settings />} />
        <Route path='/integrations/*' element={<AppStore />} />
        <Route path='/templates' element={<Templates />} />
        <Route path='/templates/bulk-import' element={<BulkImportTemplate />} />
        <Route path='/code-editor' element={<LambdaStudio />} />
        <Route path='/contacts' element={<Users />} />
        <Route path='/convobot' element={<FullPageIframe />} />
        <Route path='/chatbot/*' element={<Chatbot />} />
        <Route path='/meta-ai/*' element={<MetaAI />} />
        <Route path='/refer-earn' element={<ReferralComponent />} />
        <Route path='/admin' element={<Admin />} />
        <Route path='/accept-invite/:token' element={<AcceptInvite />} />
        <Route path='*' element={<NotFoundPage />} />
      </Routes>
    );
  } else if (authData?.isBusinessIdNotInToken) {
    return (
      <Routes>
        <Route path='/' element={<ChooseBusiness />} />
        <Route path='/accept-invite/:token' element={<AcceptInvite />} />
        <Route path='*' element={<Navigate to='/' />} />
      </Routes>
    );
  } else {
    return (
      <Routes>
        <Route path='/' element={<Auth />} />
        <Route path='/oauth/authorize' element={<OAuthForm />} />
        <Route path='/register' element={<Register />} />
        <Route path='integrations/sso' element={<AutoLogin />} />
        <Route path='/reset-password' element={<ResetPassword />} />
        <Route path='/reset-password/:token' element={<UpdatePassword />} />
        <Route path='/accept-invite/:token' element={<AcceptInvite />} />
        <Route path='*' element={<NotFoundPage />} />
      </Routes>
    );
  }
};

const useSocketMessages = () => {
  const dispatch = useDispatch();
  const { actions } = useInboxPageSlice();
  const { actions: callsActions } = useCallsSlice();
  const { socket } = useSocket();

  React.useEffect(() => {
    if (socket === null) return;
    // Handlers for socket events
    const handleReceiveMsg = (newData: {
      message: Message;
      client?: Client;
      isNewClient?: boolean;
    }) => {
      dispatch(actions.updateClientListOnReceive(newData));
    };

    const handleUpdateMsg = (newData: {
      clientWaNumber: string;
      message: Message;
      conversationExpire?: Date;
      latestMessage?: { text: string; type: string; timestamp: Date };
    }) => {
      dispatch(actions.updateStatusOnSend(newData));
    };

    const handleUpdateClient = (newData: Client) => {
      dispatch(actions.updateClientField(newData));
    };

    const handleError = (error: string) => {
      errorModal(error);
    };

    const updateTemplate = (template: Template) => {
      dispatch(actions.addOrUpdateTemplate(template));
    };

    // Call handlers
    const handleIncomingCall = (callData: ReceivedCallType) => {
      console.log('Incoming call received:', callData);
      handleReceiveMsg({
        message: callData.message,
        client: createClientObject({
          clientWaNumber: callData.clientWaNumber,
          username: callData.username || callData.clientWaNumber,
        }),
      });
      dispatch(callsActions.setIncomingCall(callData));
    };

    const handleCallStatus = (callData: CallStatusType) => {
      // Update the call status in the store
      console.log('Call status received:', callData);
      dispatch(callsActions.handleCallStatus(callData));
    };

    // Subscribing to socket events
    socket.on('receiveMsg', handleReceiveMsg);
    socket.on('receiveStatus', handleUpdateMsg);
    socket.on('showErrors', handleError);
    socket.on('updateClient', handleUpdateClient);
    socket.on('updateTemplate', updateTemplate);
    socket.on('receiveCall', handleIncomingCall);
    socket.on('callStatus', handleCallStatus);

    // Cleanup function to unsubscribe from socket events
    return () => {
      socket.off('receiveMsg', handleReceiveMsg);
      socket.off('receiveStatus', handleUpdateMsg);
      socket.off('showErrors', handleError);
      socket.off('updateClient', handleUpdateClient);
      socket.off('updateTemplate', updateTemplate);
      socket.off('receiveCall', handleIncomingCall);
      socket.off('callStatus', handleCallStatus);
    };
  }, [actions, dispatch, socket, callsActions]);
};

const useTokenVerification = () => {
  const [userToken, setUserToken] = React.useState<string | null>(() =>
    getActiveToken(),
  );
  const dispatch = useDispatch();
  const { actions } = useAuthPageSlice();
  const { isPartnerDashboard } = getDomainConfig();

  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const authTokenFromURL = urlParams.get('auth_token');
    const cookie = urlParams.get('cookie_token');
    if (cookie) {
      localStorage.setItem('cookie', cookie);
      document.cookie = `connect.sid=${cookie}`;
    }
    if (authTokenFromURL) {
      setUserToken('Bearer ' + authTokenFromURL);
    }
  }, []);

  // Cross-tab identity sync. The shared identity (`localStorage._UFT_`) changes
  // on login/logout and when the visible tab promotes its business token (so a
  // fresh tab opens the business you were last looking at — see
  // adoptTabTokenAsIdentity). Either way a tab running its own pinned token is
  // left undisturbed — only a tab with no token of its own follows the identity
  // (logout → leave, login-elsewhere → adopt) — so an active tab never reloads.
  useWindowEvent('storage', event => {
    if (event.key !== '_UFT_') return;
    if (event.newValue === null) {
      // Logged out elsewhere — only a tab relying on the shared identity (no
      // pinned token of its own) follows it out.
      if (!getTabToken()) window.location.href = '/';
      return;
    }
    // Logged in elsewhere — adopt only if this tab isn't already running.
    if (!getTabToken()) window.location.reload();
  });

  // Explicit "log out everywhere" signal — clears every tab regardless of its
  // own pinned token (distinct from one tab's session merely expiring).
  React.useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel('auth_channel');
    channel.onmessage = event => {
      if (event.data === 'logged_out') {
        clearAllTokens();
        window.location.href = '/';
      }
    };
    return () => channel.close();
  }, []);

  React.useEffect(() => {
    if (userToken) {
      // Persist as the shared identity only when this tab hasn't pinned its own
      // business token — otherwise a per-tab (parallel) business token would
      // clobber the shared identity that other tabs fall back to.
      if (!getTabToken()) setIdentityToken(userToken);
      const info: JwtPayload = jwtDecode(userToken);
      if (info.iat && info.iat <= 1753804370) {
        clearAllTokens();
        window.location.href = '/';
      }
      if (Date.now() >= info.exp * 1000) {
        clearAllTokens();
        window.location.href = '/';
      } else {
        info.token = userToken;
        // Partner dashboard: just need auth, skip business checks
        if (isPartnerDashboard) {
          info.isLoggedIn = true;
          info.isBusinessIdNotInToken = false;
          dispatch(actions.signInSuccess(info));
          return;
        }
        if (!info?.business?.id) {
          info.isBusinessIdNotInToken = true;
          dispatch(actions.signInSuccess(info));
          dispatch(actions.getOrgBusinessStart());
          return;
        }
        info.isLoggedIn = true;
        info.isBusinessIdNotInToken = false;
        dispatch(actions.signInSuccess(info));
        dispatch(
          inboxPageActions.updateSentBy({
            id: info.id,
            name: info.name,
            businessId: info.business.id,
          }),
        );
        // dispatch(actions.updateJwtTokenStart());
        dispatch(actions.getBusinessDetails());
        dispatch(actions.getCurrentEmployeeStart());
        dispatch(actions.getRolePermissionListStart());
        dispatch(inboxPageActions.getTemplatesStart());
        dispatch(inboxPageActions.getTemplateMediaLinkStart());
      }
    }
  }, [userToken, dispatch, actions, isPartnerDashboard]);
};

const useDisablePinchZoom = () => {
  const location = useLocation();
  React.useEffect(() => {
    const htmlElement = document.documentElement;
    const bodyElement = document.body;
    if (location.pathname === '/convobot') {
      htmlElement.style.setProperty('overscroll-behavior-x', 'none');
      bodyElement.style.setProperty('overscroll-behavior-x', 'none');
    } else {
      htmlElement.style.setProperty('overscroll-behavior-x', 'auto');
      bodyElement.style.setProperty('overscroll-behavior-x', 'auto');
    }
    const handleTouchStart = (event: TouchEvent) => {
      if (event.touches.length > 1) event.preventDefault();
    };
    const handleTouchMove = (event: TouchEvent) => {
      if (event.touches.length > 1) event.preventDefault();
    };
    const handleTouchEnd = (event: TouchEvent) => {
      if (event.touches.length > 1) event.preventDefault();
    };
    document.addEventListener('touchstart', handleTouchStart, {
      passive: false,
    });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      htmlElement.style.setProperty('overscroll-behavior-x', 'auto');
      bodyElement.style.setProperty('overscroll-behavior-x', 'auto');
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [location.pathname]);
};

const EMBED_WA_KEY = 'selectedClientWaNumber';
let embedResolveStarted = false;

const useEmbedBootstrap = (): EmbedBootStatus => {
  const [status, setStatus] = React.useState<EmbedBootStatus>(() =>
    embedBootStatus(
      new URLSearchParams(window.location.search).get('ref'),
      !!getActiveToken(),
    ),
  );

  React.useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get('ref');
    if (!ref || embedResolveStarted) return;
    embedResolveStarted = true;
    (async () => {
      try {
        const res = await api.post('/embed/resolve', { ref });
        const data = res?.data?.data as {
          token: string;
          clientWaNumber: string;
        };
        setTabToken(data.token);
        sessionStorage.setItem(EMBED_WA_KEY, data.clientWaNumber);
        window.location.replace(
          embedReloadTarget(window.location.pathname, window.location.search),
        );
      } catch {
        embedResolveStarted = false;
        setStatus('error');
      }
    })();
  }, []);

  return status;
};

const useEmbedAuthBoot = () => {
  const dispatch = useDispatch();
  const { actions } = useAuthPageSlice();
  React.useEffect(() => {
    const token = getActiveToken();
    if (!token) return;
    let info: JwtPayload;
    try {
      info = jwtDecode(token);
    } catch {
      return;
    }
    if (!info?.business?.id || (info.exp && Date.now() >= info.exp * 1000)) {
      return;
    }
    info.token = token;
    info.isLoggedIn = true;
    info.isBusinessIdNotInToken = false;
    dispatch(actions.signInSuccess(info));
    dispatch(
      inboxPageActions.updateSentBy({
        id: info.id,
        name: info.name,
        businessId: info.business.id,
      }),
    );
    dispatch(actions.getBusinessDetails());
    dispatch(inboxPageActions.getTemplatesStart());
    dispatch(inboxPageActions.getTemplateMediaLinkStart());
  }, [dispatch, actions]);
};

const EmbedLoader = () => (
  <div className='flex h-[var(--app-height,100vh)] items-center justify-center bg-[var(--heltar-bg-surface)]'>
    <Loader />
  </div>
);

function EmbedInboxShell() {
  const state = useSelector((s: RootState) => s?.authPage?.authData);
  const selectedClientName = useSelector((s: RootState) => {
    const c = s?.inboxPage?.selectedClient;
    return c ? c.username || c.clientWaNumber : null;
  });
  useIOSViewportFix();
  useEmbedAuthBoot();
  useSocketMessages();
  React.useEffect(() => {
    if (selectedClientName && window.parent !== window) {
      window.parent.postMessage(
        { type: 'embed-chat:title', title: selectedClientName },
        '*',
      );
    }
  }, [selectedClientName]);
  const isLoggedIn = state && state.isLoggedIn;
  if (!isLoggedIn) return <EmbedLoader />;
  return (
    <>
      <div className='h-[var(--app-height,100vh)] overflow-hidden bg-[var(--heltar-bg-surface)]'>
        <Inbox embed />
      </div>
      <GlobalStyle />
      <ToastContainer />
    </>
  );
}

function EmbedAppContent() {
  const { t } = useTranslation();
  const status = useEmbedBootstrap();
  if (status === 'resolving') return <EmbedLoader />;
  if (status === 'error') {
    return (
      <div className='flex h-[var(--app-height,100vh)] items-center justify-center bg-[var(--heltar-bg-surface)] px-6 text-center text-[var(--heltar-text-primary)]'>
        {t('This chat session has expired. Please reopen it from your CRM.')}
      </div>
    );
  }
  return <EmbedInboxShell />;
}
